import React, { useEffect, useState, useRef } from "react";
import {
  View,
  TouchableOpacity,
  StyleSheet,
  Pressable,
  Text,
} from "react-native";
import {
  Ionicons,
  MaterialCommunityIcons,
  AntDesign,
} from "@expo/vector-icons";
import Slider from "@react-native-community/slider";
import { LinearGradient } from "expo-linear-gradient";
import Colors from "../../Resources/Color";
import MediaManager from "../Classes/MediaManager";
import FullScreenVideoPlayer from "./FullScreenVideoPlayer";
import { normalizeChatVideoPlaybackError } from "../Chat/chatVideoPlaybackErrorUtils";

const VideoComponent = ({
  url,
  width,
  style,
  nativeControls = false,
  contentFit = "contain",
  loop = true,
  muted = true,
  onPlayerReady,
  isFullScreen = false,
  onFullScreenPress,
  showCustomControls = true,
  // Overlay props
  weekText,
  serieDetails,
  exerciseName,
  protocolSlot,
  protocolSlotColor,
  onPlaybackError,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [showControls, setShowControls] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const videoRef = useRef(null);
  const progressInterval = useRef(null);
  const onPlaybackErrorRef = useRef(onPlaybackError);

  const mediaManager = MediaManager.getInstance();

  useEffect(() => {
    onPlaybackErrorRef.current = onPlaybackError;
  }, [onPlaybackError]);

  const reportPlaybackError = (origin, error, videoNode = null) => {
    if (!onPlaybackErrorRef.current) return;

    const payload = normalizeChatVideoPlaybackError({
      origin,
      error,
      video: videoNode || videoRef.current,
      url,
      isFullScreen,
    });

    if (!payload.isCodecDecodeIssue) return;
    onPlaybackErrorRef.current(payload);
  };

  // Initialize video element
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    const muteState =
      mediaManager.videoMuted !== undefined ? mediaManager.videoMuted : muted;

    // Set muted BEFORE setting src
    video.muted = muteState;
    video.volume = muteState ? 0 : 1;
    video.loop = loop;
    video.playsInline = true;
    video.preload = "metadata";
    video.src = url;

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setIsVideoReady(true);
      if (onPlayerReady) {
        onPlayerReady(video);
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => !loop && setIsPlaying(false);
    const handleError = (event) => {
      reportPlaybackError("video_error_event", event, video);
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("error", handleError);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("error", handleError);
    };
  }, [url, loop, muted, onPlayerReady, isFullScreen]);

  // Sync mute state with MediaManager
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const muteState =
      mediaManager.videoMuted !== undefined ? mediaManager.videoMuted : muted;
    setIsMuted(muteState);
    video.muted = muteState;
    video.volume = muteState ? 0 : 1;
  }, [mediaManager.videoMuted, muted]);

  // Update progress while playing
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying && !isSeeking) {
      progressInterval.current = setInterval(() => {
        if (video.currentTime !== undefined) {
          setCurrentTime(video.currentTime);
        }
      }, 100);
    } else {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
    }

    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [isPlaying, isSeeking]);

  // Handle fullscreen mode start time
  useEffect(() => {
    const video = videoRef.current;
    if (video && isFullScreen && mediaManager.videoStartTime > 0) {
      video.currentTime = mediaManager.videoStartTime;
      setCurrentTime(mediaManager.videoStartTime);
    }
  }, [isFullScreen, mediaManager.videoStartTime]);

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      const muteState =
        mediaManager.videoMuted !== undefined ? mediaManager.videoMuted : muted;
      video.muted = muteState;
      video.volume = muteState ? 0 : 1;
      video.play().catch((error) => {
        console.log("Play failed:", error);
        reportPlaybackError("video_play_rejected", error, video);
      });
    }
  };

  const handleMuteToggle = () => {
    const video = videoRef.current;
    if (!video) return;

    const newMutedState = !isMuted;
    video.muted = newMutedState;
    video.volume = newMutedState ? 0 : 1;

    setIsMuted(newMutedState);
    mediaManager.setVideoMuted(newMutedState);
  };

  const handleVideoPress = () => {
    if (showCustomControls) {
      handlePlayPause();
    }
  };

  const handleFullScreenPress = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) video.pause();
    mediaManager.openFullScreenVideo(currentTime);
    video.currentTime = 0;
    setCurrentTime(0);

    if (onFullScreenPress) onFullScreenPress();
  };

  const handleClose = () => {
    mediaManager.closeFullScreenVideo();
    mediaManager.closeMediaPlayer();
  };

  const handleMinimize = () => {
    mediaManager.closeFullScreenVideo();
  };

  const handleMouseEnter = () => showCustomControls && setShowControls(true);
  const handleMouseLeave = () => showCustomControls && setShowControls(false);
  const handleSliderValueChange = (value) => setCurrentTime(value);
  const handleSlidingStart = () => setIsSeeking(true);

  const handleSlidingComplete = (value) => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = value;
    }
    setIsSeeking(false);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getObjectFit = () => {
    switch (contentFit) {
      case "cover":
        return "cover";
      case "fill":
        return "fill";
      case "contain":
      default:
        return "contain";
    }
  };

  if (nativeControls) {
    return (
      <View style={[{ width }, style, styles.container]}>
        <video
          ref={videoRef}
          style={{
            width: "100%",
            height: "100%",
            objectFit: getObjectFit(),
          }}
          controls
          loop={loop}
          muted={muted}
          playsInline
        />
      </View>
    );
  }

  return (
    <View
      style={[{ width }, style, styles.container]}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <video
        ref={videoRef}
        style={{
          width: "100%",
          height: "100%",
          objectFit: getObjectFit(),
          maxWidth: width,
        }}
        autoPlay
        muted={muted}
        playsInline
        preload="metadata"
      />

      {/* Permanent Overlays */}
      {/* Top Right Buttons - Only in FullScreen */}
      {isFullScreen && (
        <View style={styles.topRightButtons}>
          {/* Minimize Button */}
          <TouchableOpacity style={styles.topButton} onPress={handleMinimize}>
            <MaterialCommunityIcons
              name="arrow-collapse"
              size={20}
              color={Colors.whiteColor}
            />
          </TouchableOpacity>

          {/* Close Button */}
          <TouchableOpacity style={styles.topButton} onPress={handleClose}>
            <AntDesign name="close" size={20} color={Colors.whiteColor} />
          </TouchableOpacity>
        </View>
      )}

      {/* Week Badge - Top Left */}
      {weekText && (
        <View style={styles.weekBadge}>
          <Text style={styles.weekText} numberOfLines={2}>
            {weekText}
          </Text>
        </View>
      )}

      {/* Exercise Info - Bottom Left, Above Controls */}
      {isVideoReady && (serieDetails || exerciseName || protocolSlot) && (
        <View style={styles.exerciseInfoContainer}>
          {serieDetails && (
            <View style={styles.serieDetailsContainer}>{serieDetails}</View>
          )}
          {exerciseName && (
            <Text style={styles.exerciseNameText} numberOfLines={2}>
              {exerciseName}
            </Text>
          )}
          {protocolSlot && (
            <Text
              style={[
                styles.protocolSlotText,
                protocolSlotColor && { backgroundColor: protocolSlotColor },
              ]}
              numberOfLines={1}
            >
              {protocolSlot}
            </Text>
          )}
        </View>
      )}

      {/* Custom Controls */}
      {showCustomControls && showControls && (
        <View style={styles.controlsOverlay}>
          {/* Gradient Background for Controls */}
          <LinearGradient
            colors={["rgba(0, 0, 0, 0)", "rgba(0, 0, 0, 0.8)"]}
            style={styles.controlsGradient}
            pointerEvents="none"
          />

          {/* Bottom Controls Container */}
          <View style={styles.bottomControlsContainer}>
            {/* Top Row: Play/Pause, Time, Spacer, Fullscreen, Mute */}
            <View style={styles.topRow}>
              {/* Play/Pause Button */}
              <TouchableOpacity
                style={styles.smallControlButton}
                onPress={handlePlayPause}
              >
                <Ionicons
                  name={isPlaying ? "pause" : "play"}
                  size={16}
                  color={Colors.whiteColor}
                />
              </TouchableOpacity>

              {/* Time Display */}
              <Text style={styles.timeText}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </Text>

              <View style={styles.spacer} />

              {/* Fullscreen Button */}
              {!isFullScreen && (
                <TouchableOpacity
                  style={styles.smallControlButton}
                  onPress={handleFullScreenPress}
                >
                  <MaterialCommunityIcons
                    name="arrow-expand"
                    size={16}
                    color={Colors.whiteColor}
                  />
                </TouchableOpacity>
              )}

              {/* Mute/Unmute Button */}
              <TouchableOpacity
                style={styles.smallControlButton}
                onPress={handleMuteToggle}
              >
                <Ionicons
                  name={isMuted ? "volume-mute" : "volume-high"}
                  size={16}
                  color={Colors.whiteColor}
                />
              </TouchableOpacity>
            </View>

            {/* Bottom Row: Slider */}
            <Slider
              style={styles.slider}
              minimumValue={0}
              maximumValue={duration || 1}
              value={currentTime}
              onValueChange={handleSliderValueChange}
              onSlidingStart={handleSlidingStart}
              onSlidingComplete={handleSlidingComplete}
              minimumTrackTintColor={Colors.whiteColor}
              maximumTrackTintColor="rgba(255, 255, 255, 0.3)"
              thumbTintColor={Colors.whiteColor}
              thumbStyle={styles.sliderThumb}
            />
          </View>
        </View>
      )}

      {/* FullScreen Video Player */}
      {!isFullScreen && (
        <FullScreenVideoPlayer
          url={url}
          onPlaybackError={onPlaybackError}
          weekText={weekText}
          serieDetails={serieDetails}
          exerciseName={exerciseName}
          protocolSlot={protocolSlot}
          protocolSlotColor={protocolSlotColor}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "relative",
    flex: 1,
    height: "100%",
    width: "100%",
  },
  videoWrapper: {
    width: "100%",
    height: "100%",
  },
  video: {
    width: "100%",
    height: "100%",
  },
  controlsOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: "flex-end",
    alignItems: "center",
    backgroundColor: "transparent",
    pointerEvents: "box-none",
  },
  controlsGradient: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 120,
    pointerEvents: "none",
  },
  bottomControlsContainer: {
    position: "absolute",
    bottom: 12,
    left: 12,
    right: 12,
    borderRadius: 12,
  },
  topRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  smallControlButton: {
    width: 27,
    height: 27,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    borderRadius: 16,
  },
  bottomControlButton: {
    padding: 6,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    borderRadius: 20,
  },
  spacer: {
    flex: 1,
  },
  slider: {
    width: "100%",
    height: 20,
    marginHorizontal: 0,
  },
  sliderThumb: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  timeText: {
    color: Colors.whiteColor,
    fontSize: 11,
    fontWeight: "500",
    marginLeft: 4,
  },
  topRightButtons: {
    position: "absolute",
    top: 12,
    right: 12,
    zIndex: 10,
    flexDirection: "row",
    gap: 12,
  },
  topButton: {
    // padding: 10,
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    borderRadius: 20,
  },
  weekBadge: {
    position: "absolute",
    top: 12,
    left: 12,
    height: 26,
    alignSelf: "flex-start",
  },
  weekText: {
    fontSize: 11,
    color: Colors.blackColor,
    padding: 6,
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    borderRadius: 10,
  },
  exerciseInfoContainer: {
    position: "absolute",
    bottom: 66,
    left: 12,
    right: 12,
    gap: 4,
    alignItems: "flex-start",
  },
  serieDetailsContainer: {
    padding: 4,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    alignSelf: "flex-start",
    borderRadius: 8,
    flexDirection: "row",
    alignItems: "center",
  },
  exerciseNameText: {
    padding: 4,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    alignSelf: "flex-start",
    borderRadius: 8,
    fontSize: 11,
    color: Colors.whiteColor,
    flexShrink: 0,
  },
  protocolSlotText: {
    padding: 4,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    alignSelf: "flex-start",
    borderRadius: 8,
    fontSize: 11,
    color: Colors.whiteColor,
    flexShrink: 0,
  },
});

export default VideoComponent;
